import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import API from '../../api/api';

const WellInfoForm = ({ jobId, formData, setFormData, setWellId, setActiveTab }) => {
  const [wellTypes, setWellTypes] = useState([]);
  const [elevationReferences, setElevationReferences] = useState([]);
  const [northReferences, setNorthReferences] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Derive editing state from formData
  const isEditing = !!formData.id;

  // Set default values only when creating new well info (not when editing existing)
  useEffect(() => {
    if (!isEditing) {
      setFormData((prev) => ({
        ...prev,
        // Only set defaults if the field is empty/undefined
        geodetic_system: prev.geodetic_system || "Universal Transverse Mercator",
        geodetic_datum: prev.geodetic_datum || "PSD 93",
        map_zone: prev.map_zone || "Zone 40N(54E to 60E)",
        reference_datum: prev.reference_datum || "RKB/DFE(Drill Floor Elevation)",
      }));
    }
  }, [isEditing, setFormData]);

  // Fetch well types, elevation references, and north references
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [elevationRes, northRes] = await Promise.all([
          API.get('/elivationreference/'),
          API.get('/northreference/')
        ]);

        setElevationReferences(elevationRes.data);
        setNorthReferences(northRes.data);

        // Only set default references if we're creating new well info
        if (!isEditing) {
          setFormData((prev) => {
            let updated = { ...prev };
            
            // Default North Reference = "Grid North" (only if not already set)
            if (!prev.north_reference) {
              const gridNorth = northRes.data.find(
                (ref) => ref.north_reference_name.toLowerCase() === "grid north"
              );
              if (gridNorth) updated.north_reference = gridNorth.north_reference_id;
            }

            // Default Elevation Reference = "Mean Sea Level" (only if not already set)
            if (!prev.elevation_referred_to) {
              const meanSeaLevel = elevationRes.data.find(
                (ref) => ref.elivation_reference_name.toLowerCase().includes("mean sea level")
              );
              if (meanSeaLevel) updated.elevation_referred_to = meanSeaLevel.elivation_reference_id;
            }

            return updated;
          });
        }
      } catch (error) {
        toast.error('Error fetching data');
        console.error('Fetch error:', error);
      }
    };
    
    fetchData();
  }, [isEditing, setFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const sanitize = (data) => {
    const sanitized = {};
    for (const key in data) {
      let value = data[key];
      sanitized[key] = value === '' ? null : isNaN(value) ? value : Number(value);
    }
    return sanitized;
  };

  const allRequiredFieldsFilled = [
    'latitude_degrees', 'latitude_minutes', 'latitude_seconds',
    'longitude_degrees', 'longitude_minutes', 'longitude_seconds',
    'northing_value', 'easting_value',
    'ground_level_elevation', 'central_meridian', 
    'elevation_referred_to', 'north_reference'
  ].every(field => {
    const value = formData[field];
    return value !== null && value !== undefined && value.toString().trim() !== '';
  });

  const handleSubmit = async () => {
    if (!jobId) {
      toast.error('Job ID is missing. Cannot submit.');
      return;
    }

    setSaving(true);
    const payload = sanitize({ ...formData, job_number: jobId });

    try {
      if (isEditing) {
        await API.put(`/wellinfo/${formData.id}/`, payload);
        toast.success('Well info updated successfully!');
      } else {
        const response = await API.post('/wellinfo/', payload);
        const newWellInfo = response.data;
        setFormData(newWellInfo); // Update parent state with new data
        setWellId(newWellInfo.id); // Set well ID for other components
        toast.success('Well info saved successfully!');
      }
      setActiveTab('Run Info');
    } catch (err) {
      console.error('API Error:', err.response?.data || err.message);
      toast.error(
        err.response?.data?.detail ||
          JSON.stringify(err.response?.data) ||
          'Failed to submit well info.'
      );
    } finally {
      setSaving(false);
    }
  };

  const toDecimalDegrees = (deg, min, sec) => {
    const d = parseFloat(deg);
    const m = parseFloat(min);
    const s = parseFloat(sec);
    if (isNaN(d) || isNaN(m) || isNaN(s)) return '';
    return (d + m / 60 + s / 3600).toFixed(6);
  };

  return (
    <div className="space-y-6 text-xs">
      <div className="rounded-md p-6 shadow-sm relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <h2 className="text-sm font-bold mb-3">Well Center Coordinates</h2>
            <div className="mb-3">
              <label className="block text-[11px] font-medium mb-1">Latitude</label>
              <div className="flex items-center gap-2 flex-wrap">
                {['degrees', 'minutes', 'seconds'].map((part, idx) => (
                  <div key={`lat-${part}`} className="flex items-center gap-1">
                    <input
                      type="number"
                      name={`latitude_${part}`}
                      value={formData[`latitude_${part}`] || ''}
                      onChange={handleChange}
                      placeholder={part[0].toUpperCase() + part.slice(1)}
                      className={`w-${idx === 2 ? '20' : '16'} border px-2 py-1.5 rounded-lg text-xs`}
                    />
                    <span className="text-xs">{['°', '′', '″'][idx]}</span>
                  </div>
                ))}
                <span className="mx-1 text-xs font-bold">=</span>
                <input
                  type="text"
                  readOnly
                  value={toDecimalDegrees(
                    formData.latitude_degrees,
                    formData.latitude_minutes,
                    formData.latitude_seconds
                  )}
                  className="w-28 border px-2 py-1.5 rounded-lg bg-gray-100 text-xs"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium mb-1">Longitude</label>
              <div className="flex items-center gap-2 flex-wrap">
                {['degrees', 'minutes', 'seconds'].map((part, idx) => (
                  <div key={`long-${part}`} className="flex items-center gap-1">
                    <input
                      type="number"
                      name={`longitude_${part}`}
                      value={formData[`longitude_${part}`] || ''}
                      onChange={handleChange}
                      placeholder={part[0].toUpperCase() + part.slice(1)}
                      className={`w-${idx === 2 ? '20' : '16'} border px-2 py-1.5 rounded-lg text-xs`}
                    />
                    <span className="text-xs">{['°', '′', '″'][idx]}</span>
                  </div>
                ))}
                <span className="mx-1 text-xs font-bold">=</span>
                <input
                  type="text"
                  readOnly
                  value={toDecimalDegrees(
                    formData.longitude_degrees,
                    formData.longitude_minutes,
                    formData.longitude_seconds
                  )}
                  className="w-28 border px-2 py-1.5 rounded-lg bg-gray-100 text-xs"
                />
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-sm font-bold mb-3">UTM Coordinates</h2>
            {['northing_value', 'easting_value'].map((field) => (
              <div className="mb-4" key={field}>
                <label className="block text-[11px] font-medium mb-1 capitalize">
                  {field.replace('_', ' ')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="any"
                    name={field}
                    value={formData[field] || ''}
                    onChange={handleChange}
                    className="w-60 border px-2 py-1.5 rounded-lg text-xs"
                  />
                  <span className="text-xs">{field === 'northing_value' ? 'mN' : 'mE'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {[
            { name: 'ground_level_elevation', label: 'Ground Level Elevation', type: 'number' },
            { name: 'central_meridian', label: 'Central Meridian', type: 'number' },
          ].map(({ name, label, type }) => (
            <div key={name}>
              <label className="block text-[11px] font-medium mb-1">{label}</label>
              <input
                type={type}
                step={type === 'number' ? 'any' : undefined}
                name={name}
                value={formData[name] || ''}
                onChange={handleChange}
                className="w-full border px-2 py-1.5 rounded-lg text-xs"
              />
            </div>
          ))}

          {/* Fixed default fields (read-only) */}
          {[
            { name: 'geodetic_system', label: 'Geodetic System' },
            { name: 'geodetic_datum', label: 'Geodetic Datum' },
            { name: 'map_zone', label: 'Map Zone' },
            { name: 'reference_datum', label: 'Reference Datum' },
          ].map(({ name, label }) => (
            <div key={name}>
              <label className="block text-[11px] font-medium mb-1">{label}</label>
              <input
                type="text"
                name={name}
                value={formData[name] || ''}
                readOnly
                className="w-full border px-2 py-1.5 rounded-lg text-xs bg-gray-100 cursor-not-allowed"
              />
            </div>
          ))}
  
          <div>
            <label className="block text-[11px] font-medium mb-1">North Reference</label>
            <select
              name="north_reference"
              value={formData.north_reference || ''}
              onChange={handleChange}
              className="w-full border px-2 py-1.5 rounded-lg text-xs bg-white"
            >
              <option value="">Select North Reference</option>
              {northReferences.map((ref) => (
                <option key={ref.north_reference_id} value={ref.north_reference_id}>
                  {ref.north_reference_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-medium mb-1">Elevation Referred To</label>
            <select
              name="elevation_referred_to"
              value={formData.elevation_referred_to || ''}
              onChange={handleChange}
              className="w-full border px-2 py-1.5 rounded-lg text-xs bg-white"
            >
              <option value="">Select Elevation Reference</option>
              {elevationReferences.map((ref) => (
                <option key={ref.elivation_reference_id} value={ref.elivation_reference_id}>
                  {ref.elivation_reference_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSubmit}
            disabled={saving || !allRequiredFieldsFilled}
            className={`px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all ${
              saving || !allRequiredFieldsFilled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {saving 
              ? (isEditing ? 'Updating...' : 'Saving...') 
              : (isEditing ? 'Update Well Info' : 'Save Well Info')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default WellInfoForm;